// The Hubs page: a table of hubs with create / edit / delete.
//
// Data flow: load hubs for the active account on mount; the form (a modal)
// handles create/edit and calls back to reload. Deletion confirms first.

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Spinner } from "../../components/ui";
import { fmtN } from "../../lib/format";
import { getErrorMessage } from "../../lib/errors";
import type { Hub } from "../../lib/types";
import { listHubs, setHubActive } from "./hubsApi";
import HubForm from "./HubForm";

export default function HubList() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [hubs, setHubs] = useState<Hub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state: undefined = closed, null = creating, Hub = editing.
  const [formFor, setFormFor] = useState<Hub | null | undefined>(undefined);
  const [showInactive, setShowInactive] = useState(true);

  const reload = useCallback(async () => {
    if (!accountId) {
      // No active account yet — don't get stuck on the loading state.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setHubs(await listHubs(accountId));
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load hubs."));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function toggleActive(hub: Hub) {
    if (!accountId) return;
    try {
      await setHubActive(accountId, hub.id, !hub.active);
      await reload();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to update hub."));
    }
  }

  const visibleHubs = showInactive ? hubs : hubs.filter((h) => h.active);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Hubs</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <Button onClick={() => setFormFor(null)}>+ New hub</Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="p-0">
        {loading ? (
          <Spinner />
        ) : hubs.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">
            No hubs yet. Click <span className="font-medium">New hub</span> to add your first one.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Code</th>
                <th className="px-5 py-3 font-medium">Location</th>
                <th className="px-5 py-3 font-medium">Region</th>
                <th className="px-5 py-3 font-medium">Manager</th>
                <th className="px-5 py-3 font-medium">Capacity</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleHubs.map((hub) => (
                <tr key={hub.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{hub.name}</td>
                  <td className="px-5 py-3 text-slate-600">{hub.code ?? "—"}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {[hub.city, hub.state].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{hub.region ?? "—"}</td>
                  <td className="px-5 py-3 text-slate-600">
                    <div>{hub.manager ?? "—"}</div>
                    {hub.managerEmail && (
                      <a
                        href={`mailto:${hub.managerEmail}`}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        {hub.managerEmail}
                      </a>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {hub.capacity != null ? fmtN(hub.capacity) : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        hub.active
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {hub.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setFormFor(hub)}>
                        Edit
                      </Button>
                      <Button variant="ghost" onClick={() => toggleActive(hub)}>
                        {hub.active ? "Pause" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {formFor !== undefined && (
        <HubForm
          key={formFor?.id ?? "new"}
          accountId={accountId}
          hub={formFor}
          existingHubs={hubs}
          onClose={() => setFormFor(undefined)}
          onSaved={() => {
            setFormFor(undefined);
            reload();
          }}
        />
      )}
    </div>
  );
}
